import { Box, Typography, Grid, Chip, Avatar, Stack, useTheme, IconButton, Tooltip } from '@mui/material';
import { Person, Payment, LocalOffer as LocalOfferIcon } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Card } from '../../../../shared/components/ui/Card';
import type { Participant } from '../../../../shared/services/visitors/visitorService';
import { formatBRL } from '../../../../utils/formatBRL';

interface VisitorInfoProps {
  participant: Participant;
  actions?: React.ReactNode;
  onEditAnswers?: () => void;
}

export function VisitorInfo({ participant, actions, onEditAnswers }: VisitorInfoProps) {
  const theme = useTheme();

  const getPaymentStatusLabel = (status: 'open' | 'confirmed' | 'paid' | 'refunded') => {
    switch (status) {
      case 'paid': return 'Pago';
      case 'confirmed': return 'Confirmado';
      case 'open': return 'Em aberto';
      case 'refunded': return 'Estornado';
      default: return status;
    }
  };

  const getPaymentStatusColor = (status: 'open' | 'confirmed' | 'paid' | 'refunded') => {
    switch (status) {
      case 'paid': return 'success';
      case 'confirmed': return 'info';
      case 'open': return 'warning';
      case 'refunded': return 'error';
      default: return 'default';
    }
  };

  const getPaymentMethodLabel = (method: 'card' | 'pix' | 'cash' | 'boleto' | null) => {
    if (!method) return '-';
    switch (method) {
      case 'card': return 'Cartão';
      case 'pix': return 'PIX';
      case 'cash': return 'Dinheiro';
      case 'boleto': return 'Boleto';
      default: return method;
    }
  };

  
  function getInstallmentStatusLabel(status: string) {
    switch (status) {
      case 'PAID': return 'Pago';
      case 'PENDING': return 'Pendente';
      case 'OVERDUE': return 'Vencida';
      case 'CANCELLED': return 'Cancelada';
      default: return status;
    }
  }
  
  function getInstallmentStatusColor(status: string) {
    switch (status) {
      case 'PAID': return 'success';
      case 'PENDING': return 'warning';
      case 'OVERDUE': return 'error';
      case 'CANCELLED': return 'default';
      default: return 'default';
    }
  }

  return (
    <>
      {/* Topo: Avatar, info e ações lado a lado */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Avatar sx={{ width: 110, height: 110, fontSize: 48, bgcolor: 'primary.main' }}>
            {participant.name?.[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography sx={{ color: theme.palette.text.primary, fontWeight: 800, fontSize: 28, letterSpacing: -1 }}>
              {participant.name}
            </Typography>
            <Typography sx={{ color: theme.palette.text.secondary, fontSize: 16, fontWeight: 500 }}>
              Participante
            </Typography>
            <Stack direction="row" spacing={2} mt={1} alignItems="center">
              {participant.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ color: theme.palette.text.secondary, fontSize: 14 }}>{participant.email}</Typography>
                </Box>
              )}
              <Chip
                label={getPaymentStatusLabel(participant.payment_status)}
                color={getPaymentStatusColor(participant.payment_status) as any}
                size="small"
                variant="filled"
              />
            </Stack>
          </Box>
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: 2, sm: 0 } }}>
          {actions}
        </Box>
      </Box>

      {/* Detalhes */}
      <Grid container spacing={3}>
        {/* Informações Pessoais */}
        <Grid item xs={12} md={6}>
          <Card sx={{ background: theme.palette.background.paper, borderRadius: 3, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Person sx={{ color: theme.palette.primary.main, fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color={theme.palette.text.primary}>Dados do Participante</Typography>
            </Box>
            <Box component="dl" sx={{ m: 0 }}>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontSize: 15 }}>Nome:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontWeight: 500, fontSize: 15 }}>{participant.name}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontSize: 15 }}>CPF:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontWeight: 500, fontSize: 15 }}>{participant.cpf}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontSize: 15 }}>E-mail:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontWeight: 500, fontSize: 15 }}>{participant.email}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontSize: 15 }}>Tipo de ingresso:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontWeight: 500, fontSize: 15 }}>
                  {(() => {
                    switch (participant.ticket_type) {
                      case 'inteiro': return participant.event?.label_ingresso_inteiro || 'Inteiro';
                      case 'meia': return participant.event?.label_ingresso_meia || 'Meia';
                      case 'estudante': return participant.event?.label_ingresso_estudante || 'Estudante';
                      case 'crianca': return participant.event?.label_ingresso_crianca || 'Criança';
                      default: return participant.ticket_type;
                    }
                  })()}
                </dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontSize: 15 }}>Valor:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontWeight: 500, fontSize: 15 }}>
                  {participant.discount_applied && Number(participant.discount_applied) > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>R$ {Number(participant.ticket_value).toFixed(2).replace('.', ',')}</span>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          textDecoration: 'line-through', 
                          color: 'text.disabled' 
                        }}
                      >
                        (era R$ {Number(participant.original_ticket_value).toFixed(2).replace('.', ',')})
                      </Typography>
                    </Box>
                  ) : (
                    `R$ ${Number(participant.ticket_value).toFixed(2).replace('.', ',')}`
                  )}
                </dd>
              </Box>
              {participant.discount_applied && Number(participant.discount_applied) > 0 && (
                <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                  <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontSize: 15 }}>Cupom:</dt>
                  <dd style={{ margin: 0, marginLeft: 16 }}>
                    <Chip
                      label={`-R$ ${Number(participant.discount_applied).toFixed(2).replace('.', ',')} de desconto`}
                      color="success"
                      size="small"
                      variant="filled"
                      icon={<LocalOfferIcon />}
                    />
                  </dd>
                </Box>
              )}
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontSize: 15 }}>Data de cadastro:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontWeight: 500, fontSize: 15 }}>
                  {participant.created_at ? format(new Date(participant.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR }) : '-'}
                </dd>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Informações de Pagamento */}
        <Grid item xs={12} md={6}>
          <Card sx={{ background: theme.palette.background.paper, borderRadius: 3, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Payment sx={{ color: theme.palette.primary.main, fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color={theme.palette.text.primary}>Informações de Pagamento</Typography>
            </Box>
            <Box component="dl" sx={{ m: 0 }}>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontSize: 15 }}>Status:</dt>
                <dd style={{ margin: 0, marginLeft: 16 }}>
                  <Chip
                    label={getPaymentStatusLabel(participant.payment_status)}
                    color={getPaymentStatusColor(participant.payment_status) as any}
                    size="small"
                    variant="filled"
                  />
                </dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontSize: 15 }}>Método:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontWeight: 500, fontSize: 15 }}>
                  {getPaymentMethodLabel(participant.payment_method)}
                </dd>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Campos dinâmicos */}
        <Grid item xs={12}>
          <Card sx={{ background: theme.palette.background.paper, borderRadius: 3, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 22, mr: 1 }} />
                <Typography fontWeight={700} fontSize={17} color={theme.palette.text.primary}>
                  Respostas adicionais
                </Typography>
              </Box>
              {onEditAnswers && (
                <Tooltip title="Editar respostas" arrow>
                  <IconButton 
                    size="small" 
                    onClick={onEditAnswers}
                    sx={{ 
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main + '10',
                      }
                    }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            
            {participant.dynamicAnswers && participant.dynamicAnswers.length > 0 ? (
              <Stack spacing={2}>
                {participant.dynamicAnswers.map((answer: any, idx: number) => (
                  <Box
                    key={idx}
                    sx={{
                      background: theme.palette.background.default,
                      borderRadius: 2,
                      boxShadow: '0 1px 4px 0 rgba(16,30,54,0.04)',
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant="subtitle2" color="primary.main" fontWeight={600} sx={{ mb: 0.5 }}>
                      {answer.question}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" fontWeight={500}>
                      {answer.answer}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Nenhuma resposta adicional cadastrada
              </Typography>
            )}
          </Card>
        </Grid>

        {/* Parcelas */}
        {participant?.installments && participant?.installments.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ background: theme.palette.background.paper, borderRadius: 3, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography fontWeight={700} fontSize={17} color={theme.palette.text.primary}>
                  Parcelas
                </Typography>
              </Box>
              <Stack spacing={1}>
                {participant?.installments.map((inst: any) => (
                  <Box
                    key={inst.id}
                    sx={{
                      background: theme.palette.background.default,
                      borderRadius: 2,
                      boxShadow: '0 1px 4px 0 rgba(16,30,54,0.04)',
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} color="primary.main">
                      {inst?.installment_number}/{inst?.total_installments}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Vencimento: {inst?.due_date ? format(new Date(inst?.due_date), "dd/MM/yyyy") : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Valor: {formatBRL(inst?.value)}
                    </Typography>
                    <Chip
                      label={getInstallmentStatusLabel(inst?.status)}
                      color={getInstallmentStatusColor(inst?.status) as any}
                      size="small"
                      variant="filled"
                    />
                  </Box>
                ))}
              </Stack>
            </Card>
          </Grid>
        )}
      </Grid>
    </>
  );
}